import { cssAnchorProps } from "@circle-react/helpers/cssAnchorHelpers";
import { Drawer } from "@circleco/compass/components/Drawer";
import { MobileTopNav } from "@circleco/compass/components/MobileTopNav";
import type { MobileTopNavAction } from "@circleco/compass/components/MobileTopNav";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import type { MobileTrailingActionsWithOptions } from "@/react/components/Mobile/context/sectionTabs/types";
import { useMobileChromeVisibility } from "@/react/components/Mobile/hooks/chrome/useMobileChromeVisibility";
import { hasStructuredActions } from "@/react/components/Mobile/shell/topBar/hasStructuredActions";
import { UserImage } from "@/react/components/SettingsApp/CompassCompat/UserImage";

export interface MobileDefaultTopBarProps {
  avatarName: string;
  avatarUrl?: string;
  communityName: string;
  isHidden?: boolean;
  logoUrl: string | undefined;
  onTitleClick?: () => void;
  title: string;
  /**
   * A registrar's trailing content, in one of three shapes:
   * - `MobileTopNavAction[]` (preferred): passed straight through to
   *   `MobileTopNav`'s own `actions`, so it gets real collapsing behavior -
   *   e.g. into an overflow menu once the count including the avatar
   *   exceeds `collapseThreshold`. See `NotificationsTrailingActions`.
   * - `MobileTrailingActionsWithOptions` (same structured form, plus an
   *   explicit `collapseActions` override): for a page that needs to force
   *   every action inline instead of collapsing, e.g. Messages/Notifications.
   * - Plain `ReactNode` (legacy): rendered inline next to the avatar, inside
   *   the same never-collapsing slot, for registrars not yet migrated to the
   *   structured form (e.g. `SpaceTopBarRegistrar`'s many space-header
   *   consumers).
   */
  trailing?:
    | ReactNode
    | MobileTopNavAction[]
    | MobileTrailingActionsWithOptions;
}

export const MobileDefaultTopBar = ({
  avatarName,
  avatarUrl,
  communityName,
  isHidden = false,
  logoUrl,
  onTitleClick,
  title,
  trailing,
}: MobileDefaultTopBarProps) => {
  // Each derived value below calls `hasStructuredActions`/`Array.isArray`
  // inline against `trailing` directly (not via a shared stored boolean) so
  // TS's type-guard narrowing actually applies at each site - stashing the
  // guard's result in a variable and reusing it loses that narrowing, since
  // it's a different variable, not a guard on `trailing` itself.
  const trailingActions = hasStructuredActions(trailing)
    ? Array.isArray(trailing)
      ? trailing
      : trailing.actions
    : undefined;
  const shouldCollapseActions =
    hasStructuredActions(trailing) && !Array.isArray(trailing)
      ? trailing.collapseActions
      : undefined;
  const legacyTrailing = hasStructuredActions(trailing) ? null : trailing;

  const { isAvatarHidden } = useMobileChromeVisibility();

  const avatarButton = isAvatarHidden ? null : (
    <Drawer.Trigger>
      <button
        type="button"
        className="rounded-full focus-visible:outline-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        aria-label={t("mobile_shell.open_navigation_menu")}
      >
        <UserImage name={avatarName} src={avatarUrl} size="8" isFullRounded />
      </button>
    </Drawer.Trigger>
  );

  // Legacy ReactNode `trailing` still shares the avatar's never-collapsing
  // slot (as before); structured `trailing` goes through `actions` below
  // instead. `avatarButton` is already `null` when `isAvatarHidden` (e.g.
  // Messages/Notifications/Search), so this collapses to just
  // `legacyTrailing`, or nothing, on those pages.
  const primaryAction = legacyTrailing ? (
    <div className="flex items-center gap-x-2">
      {legacyTrailing}
      {avatarButton}
    </div>
  ) : (
    avatarButton
  );

  // Logo and title are mutually exclusive: show logo when available for the
  // current theme, fall back to the community name as text.
  const content = logoUrl
    ? {
        logo: {
          src: logoUrl,
          alt: t("community_logo_alt", { community_name: communityName }),
          imgProps: {
            id: "community-logo",
            ...cssAnchorProps({ anchor: "app-topbar-logo-image" }),
          },
        },
      }
    : { title };

  // MobileTopNav takes these as a discriminated union - both or neither.
  const titleClickProps = onTitleClick
    ? { onTitleClick, onTitleClickLabel: t("mobile_shell.switch_community") }
    : {};

  const topBar = (
    <MobileTopNav
      {...content}
      {...titleClickProps}
      actions={trailingActions}
      collapseActions={shouldCollapseActions}
      actionIconColor="primary"
      primaryAction={primaryAction}
    />
  );

  return isHidden ? (
    <div className="invisible pointer-events-none" aria-hidden="true">
      {topBar}
    </div>
  ) : (
    topBar
  );
};
